/** Performance tab - KPI cards plus a recent issues table. Reuses the existing governance
 * profile's performance metrics (`supplierGovernanceData.ts`); the "recent performance
 * issues" table is demo-only mock data. */

import { useTranslation } from 'react-i18next';
import {
  Box, Grid, Table, TableBody, TableCell, TableHead, TableRow, Typography,
} from '@mui/material';
import { Section } from '../../../../../components/ui/Section';
import { SupplierGovernanceProfile } from '../../../../../mocks/data/supplierGovernanceData';

export interface PerformanceTabProps {
  governanceProfile: SupplierGovernanceProfile;
}

interface PerformanceIssue {
  date: string;
  site: string;
  issueType: string;
  severity: string;
  status: string;
  reference: string;
  notes: string;
}

const performanceIssues: PerformanceIssue[] = [
  {
    date: '2026-07-03', site: 'Site 0102', issueType: 'Late delivery', severity: 'Medium', status: 'Open', reference: 'PO-88213', notes: 'Delivery arrived 4 hours late, no advance notice.',
  },
  {
    date: '2026-06-20', site: 'Site 0145', issueType: 'Rejected delivery', severity: 'High', status: 'Resolved', reference: 'PO-87990', notes: 'Damaged pallet rejected on arrival, credit issued.',
  },
];

const KpiCard = ({ label, value }: { label: string; value: string | number }) => (
  <Grid item
    xs={12}
    sm={6}
    md={3}>
    <Box sx={{ p: 2 }}>
      <Typography variant="textXS"
        color="text.secondary">{label}</Typography>
      <Typography variant="textMD"
        fontWeight={600}>{value}</Typography>
    </Box>
  </Grid>
);

const PerformanceTab: React.FC<PerformanceTabProps> = ({ governanceProfile }) => {
  const { t } = useTranslation('suppliers');
  const performance = governanceProfile.performance;

  return (
    <>
      <Section>
        <Grid container>
          <KpiCard label={t('onTimeDelivery', 'On-time delivery %')}
            value={performance ? `${performance.onTimeDeliveryPercentage}%` : '-'} />
          <KpiCard label={t('fillRate', 'Fulfilment %')}
            value={performance ? `${performance.fillRatePercentage}%` : '-'} />
          <KpiCard label={t('rejectedDeliveries', 'Rejected deliveries')}
            value={performanceIssues.filter((issue) => issue.issueType === 'Rejected delivery').length} />
          <KpiCard label={t('creditRequests', 'Credit requests')}
            value={1} />
          <KpiCard label={t('qualityIssues', 'Quality issues')}
            value={performance ? performance.qualityScore < 4 ? 1 : 0 : 0} />
          <KpiCard label={t('substitutionRate', 'Substitution rate')}
            value="2.1%" />
          <KpiCard label={t('priceVariance', 'Price variance')}
            value="+1.4%" />
          <KpiCard label={t('siteFeedbackScore', 'Site feedback score')}
            value={performance ? `${performance.qualityScore} / 5` : '-'} />
        </Grid>
      </Section>

      <Section>
        <Box sx={{ p: 4 }}>
          <Typography variant="textSM"
            fontWeight={600}
            sx={{ mb: 3 }}>{t('recentPerformanceIssues', 'Recent performance issues')}</Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('date', 'Date')}</TableCell>
                <TableCell>{t('site', 'Site')}</TableCell>
                <TableCell>{t('issueType', 'Issue type')}</TableCell>
                <TableCell>{t('severity', 'Severity')}</TableCell>
                <TableCell>{t('status', 'Status')}</TableCell>
                <TableCell>{t('linkedReference', 'Linked order/delivery reference')}</TableCell>
                <TableCell>{t('notes', 'Notes')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {performanceIssues.map((issue) => (
                <TableRow key={issue.reference}>
                  <TableCell>{issue.date}</TableCell>
                  <TableCell>{issue.site}</TableCell>
                  <TableCell>{issue.issueType}</TableCell>
                  <TableCell>{issue.severity}</TableCell>
                  <TableCell>{issue.status}</TableCell>
                  <TableCell>{issue.reference}</TableCell>
                  <TableCell>{issue.notes}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Section>
    </>
  );
};

export default PerformanceTab;
